/**
 * SASADM-0176 (saas-auth SASATH-0392) — a synchronous deploy refused with
 * `409 CADDY_HELD_BACK_HOST_CONFLICT`: a held-back fragment's previous routing still claims a
 * host that now belongs to another tenant / product. `error.details` names the fragment file,
 * the host and the new owner. SASADM-0177 (SASATH-0394 §2) also links the held-back entry itself
 * when `heldBackKind` / `heldBackId` arrive; without them only the owner is linkable.
 */
import { Alert, Box, Link } from '@mui/material';
import { Fragment, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { ApiError } from '@/api/errors';
import { useCopy } from '@/hooks/useCopy';

interface HeldBackConflictDetails {
  heldBack?: string;
  host?: string;
  ownerKind?: string;
  ownerId?: number;
  // SASADM-0177 (SASATH-0394 §2) — render-when-present: the held-back entry itself.
  heldBackKind?: string;
  heldBackId?: number;
  heldBackSlug?: string;
}

export function HeldBackConflictNotice({ err, onClose }: { err: ApiError; onClose: () => void }) {
  const { t } = useCopy();
  const details = (err.details.raw as { error?: { details?: HeldBackConflictDetails } } | null)
    ?.error?.details;
  const catalog = t(
    'errors.codes.CADDY_HELD_BACK_HOST_CONFLICT',
    "Routing not deployed: a held-back fragment's previous routing still claims a hostname that now belongs to someone else.",
  );

  function renderSentence(d: HeldBackConflictDetails): ReactNode {
    // Only called with a TENANT / PRODUCT owner and a numeric id (see the render gate).
    const product = d.ownerKind === 'PRODUCT';
    const ownerTo = product ? `/reference/products/${d.ownerId}` : `/tenants/${d.ownerId}`;
    const ownerLabel = product
      ? t('routingDeploy.ownerProduct', 'product #{{id}}', { id: d.ownerId ?? '' })
      : t('routingDeploy.ownerTenant', 'tenant #{{id}}', { id: d.ownerId ?? '' });
    const heldBackTo =
      typeof d.heldBackId === 'number' &&
      (d.heldBackKind === 'TENANT' || d.heldBackKind === 'PRODUCT')
        ? d.heldBackKind === 'PRODUCT'
          ? `/reference/products/${d.heldBackId}`
          : `/tenants/${d.heldBackId}`
        : null;
    const parts: Record<string, ReactNode> = {
      heldBack: heldBackTo ? (
        <Link component={RouterLink} to={heldBackTo} color="inherit">
          {d.heldBackSlug ?? d.heldBack}
        </Link>
      ) : (
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {d.heldBack}
        </Box>
      ),
      host: (
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {d.host}
        </Box>
      ),
      owner: (
        <Link component={RouterLink} to={ownerTo} color="inherit">
          {ownerLabel}
        </Link>
      ),
    };
    // The placeholders are kept through t() and swapped for the nodes above.
    const template = t(
      'routingDeploy.heldBackConflict',
      "Routing not deployed: {{heldBack}} couldn't be applied, and its previous routing still claims {{host}}, which now belongs to {{owner}}. Fix {{heldBack}}'s settings, then deploy.",
      { heldBack: '{{heldBack}}', host: '{{host}}', owner: '{{owner}}' },
    );
    return template.split(/(\{\{\w+\}\})/).map((piece, i) => {
      const name = /^\{\{(\w+)\}\}$/.exec(piece)?.[1];
      return <Fragment key={i}>{name && name in parts ? parts[name] : piece}</Fragment>;
    });
  }

  return (
    <Alert severity="error" sx={{ mb: 2 }} onClose={onClose} closeText={t('common.close', 'Close')}>
      {details?.heldBack &&
      details.host &&
      typeof details.ownerId === 'number' &&
      (details.ownerKind === 'TENANT' || details.ownerKind === 'PRODUCT') ? (
        renderSentence(details)
      ) : (
        <>
          {catalog}
          {err.details.message && (
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              {err.details.message}
            </Box>
          )}
        </>
      )}
    </Alert>
  );
}
